import { Input, Segmented } from 'antd';
import React, { FC, useEffect, useId, useState } from 'react';
import { formatNumberWithCommas } from '@/helpers/formatNumberWithCommas';
import './FeeValueInput.scss';

export type FeeValueType = 'percent' | 'currency_usd' | 'open_text';

export const FEE_NOTE_MAX_LENGTH = 200;

export interface FeeValueShape {
  type?: FeeValueType;
  value?: string;
  note?: string;
}

const TYPE_OPTIONS: Array<{ label: string; value: FeeValueType }> = [
  { label: '%', value: 'percent' },
  { label: '$', value: 'currency_usd' },
  { label: 'TEXT', value: 'open_text' },
];

const stripCommas = (value: string): string => value.replace(/,/g, '');

/**
 * TCX-867 — Royalty/Advertising Fee editor. Matches the card's two distinct
 * layouts (the mobile and desktop prototypes are genuinely different
 * components, not a CSS reflow of the same one):
 *   - `layout="inline"` (desktop): the segmented control (`% / $ / TEXT`)
 *     sits compact, to the right of the value input, same row.
 *   - `layout="stacked"` (mobile, the default): the segmented control is
 *     full-width, directly beneath the value input.
 * Below either: only when type is Percentage or Currency, an inset
 * "Note (optional)" box with a live character counter, capped at
 * `FEE_NOTE_MAX_LENGTH` — AC 4.
 *
 * Modeled as one Form.Item value (the whole `{ type, value, note }` object)
 * rather than three separate Form.Items, so the existing change-diff in
 * FinancialInformation's onFinish keeps working: one field, one comparison.
 *
 * `type` defaults to `percent` when absent — AC 7: a pre-existing profile
 * (migrated by TCX-866, always `{ type: 'percent', value }`) shows Percentage
 * preselected with no action needed. `note` always resolves to a string
 * (`value?.note ?? ''`) since `emit` spreads it on every change — an
 * untouched fee simply keeps whatever `toFeeValueShape` hydrated it with.
 *
 * `...rest` exists so `Form.Item` can clone this component and inject `id`,
 * `aria-describedby`, `aria-invalid` and `aria-required` the way it does for
 * any other control — without capturing and forwarding those onto the value
 * input, none of them land anywhere and the field has no accessible name and
 * no error association for assistive tech. Typed as antd's own `Input` props
 * (minus the ones this component redefines with a different shape) rather
 * than `Record<string, unknown>`, so a typo'd prop name still fails the build
 * instead of silently falling through `...rest` onto the DOM input.
 */
type FeeValueInputRestProps = Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange' | 'size' | 'prefix' | 'suffix'
>;

export const FeeValueInput: FC<
  {
    value?: FeeValueShape;
    onChange?: (value: FeeValueShape) => void;
    size?: 'large';
    layout?: 'inline' | 'stacked';
  } & FeeValueInputRestProps
> = ({ value, onChange, size = 'large', layout = 'stacked', ...rest }) => {
  const type = value?.type ?? 'percent';
  const rawValue = value?.value ?? '';
  const note = value?.note ?? '';
  const noteInputId = useId();

  const emit = (patch: Partial<FeeValueShape>) => {
    onChange?.({ type, value: rawValue, note, ...patch });
  };

  // Currency values are comma-grouped for display, matching the currency
  // inputs elsewhere on this form — the RAW value emitted upstream (and
  // eventually sent to the backend) never contains a comma, since the
  // backend rejects anything but plain digits/decimal for a `currency_usd`
  // value.
  const [currencyDisplay, setCurrencyDisplay] = useState('');
  useEffect(() => {
    if (type === 'currency_usd') {
      setCurrencyDisplay(formatNumberWithCommas(rawValue));
    }
  }, [rawValue, type]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (type === 'currency_usd') {
      const raw = stripCommas(input);
      setCurrencyDisplay(formatNumberWithCommas(raw));
      emit({ value: raw });
    } else {
      emit({ value: input });
    }
  };

  const showNote = type === 'percent' || type === 'currency_usd';
  const isInline = layout === 'inline';

  const handleTypeChange = (next: FeeValueType) => {
    // Switching to Open Text hides the Note field (AC 3) — but a note typed
    // under Percent/Currency would otherwise sit untouched in this value and
    // get saved anyway, publishing a note on a fee type that was never
    // supposed to carry one. Clear it here so switching to Open Text really
    // means "no note," not just "no note field visible right now."
    emit({ type: next, note: next === 'open_text' ? undefined : note });
  };

  const selector = (
    <Segmented
      block={!isInline}
      className="fee-value-type-selector"
      value={type}
      onChange={(next) => handleTypeChange(next as FeeValueType)}
      options={TYPE_OPTIONS}
    />
  );

  // Desktop: the segmented control sits INSIDE the value input's own box,
  // via its `suffix` slot — not as a sibling element next to it — so the
  // affix character and the selector share the exact same field. Mobile
  // keeps the selector as its own full-width control below (a genuinely
  // different layout, not a reflow of this one).
  const inlineSuffix = isInline ? (
    <div className="fee-value-inline-suffix">
      {type === 'percent' && <span>%</span>}
      {selector}
    </div>
  ) : undefined;

  const inputProps =
    type === 'percent'
      ? { suffix: inlineSuffix ?? '%', value: rawValue }
      : type === 'currency_usd'
        ? { prefix: '$', suffix: inlineSuffix, value: currencyDisplay }
        : { suffix: inlineSuffix, value: rawValue };

  return (
    <div className={`fee-value-input${isInline ? ' fee-value-input-inline' : ''}`}>
      <Input
        size={size}
        data-testid="fee-value-value-input"
        onChange={handleValueChange}
        {...rest}
        {...inputProps}
      />
      {!isInline && selector}
      {showNote && (
        <div className="fee-value-note">
          <label className="fee-value-note-label" htmlFor={noteInputId}>
            Note (optional)
          </label>
          <Input
            id={noteInputId}
            className="fee-value-note-input"
            data-testid="fee-value-note-input"
            variant="borderless"
            placeholder="Shows beneath the value publicly"
            value={note}
            onChange={(e) => emit({ note: e.target.value })}
            maxLength={FEE_NOTE_MAX_LENGTH}
          />
          <div className="fee-value-note-count">
            {note.length} / {FEE_NOTE_MAX_LENGTH}
          </div>
        </div>
      )}
    </div>
  );
};

/** "Royalty (%)" / "Royalty (USD)" / "Royalty" — AC 2. */
export function feeFieldLabel(
  base: string,
  type: FeeValueType | undefined,
): string {
  if (type === 'currency_usd') return `${base} (USD)`;
  if (type === 'open_text') return base;
  return `${base} (%)`;
}
